import { useMemo, useState } from "react";
import type { Item, ItemStatus, ItemType, Phase, Priority } from "@/lib/types";
import { TYPE_META, PRIORITY_META, RECUR_BADGE, STATUS_META, STATUS_ORDER } from "@/lib/types";
import { deadlineState, isOverdue, shortDate, type DeadlineState } from "@/lib/format";
import { useShowCompleted } from "@/lib/theme";
import { card, ProgressBar } from "./atoms";

type SubCount = { done: number; total: number };

// §4.1 — the header and the rows share ONE track list, and none of it is
// fractional. Fractional tracks resolve against different content in the two
// grids, so the columns drift apart as soon as a title gets long.
const COLS = "34px minmax(180px, 1fr) 96px 130px 146px 96px 120px 108px 92px";

type SortKey = "title" | "type" | "phase" | "status" | "start" | "due" | "progress" | "priority";

/**
 * Two date columns, because Todoist has two dates and they mean different
 * things: the DATE is the day you plan to work on it, the DEADLINE is the hard
 * finish. Start reads `item.start` and Deadline reads `item.due`, each with no
 * fallback to the other — a column that borrows the other field's value when
 * its own is empty is a column that lies about which date you are looking at.
 */
const HEADINGS: { key: SortKey | null; label: string }[] = [
  { key: null, label: "" },
  { key: "title", label: "Task" },
  { key: "type", label: "Type" },
  { key: "phase", label: "Phase" },
  { key: "status", label: "Status" },
  { key: "start", label: "Start" },
  { key: "due", label: "Deadline" },
  { key: "progress", label: "Progress" },
  { key: "priority", label: "Priority" },
];

// Type and priority sort by their meta order — "Deliverable, Milestone, Task"
// and "High, Low, Medium, Urgent" are not useful orderings.
const TYPE_ORDER = Object.keys(TYPE_META) as ItemType[];
const PRIORITY_ORDER = Object.keys(PRIORITY_META) as Priority[];

export function GridTab({
  items, phases, subCounts, onOpen, onToggle, onStatusChange, onDueChange, mobile,
}: {
  items: Item[];
  phases: Phase[];
  subCounts?: Map<string, SubCount>;
  onOpen: (i: Item) => void;
  onToggle?: (i: Item) => void;
  onStatusChange?: (i: Item, status: ItemStatus) => void;
  onDueChange?: (i: Item, due: string) => void;
  mobile: boolean;
}) {
  const [showCompleted, setShowCompleted] = useShowCompleted();
  // Opens on the start date — the order you will actually work through. The
  // Deadline column is one click away.
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "start", dir: 1 });

  const phaseName = useMemo(() => new Map(phases.map((p) => [p.id, p.name])), [phases]);
  const phasePos = useMemo(() => new Map(phases.map((p) => [p.id, p.position ?? 0])), [phases]);

  const progressOf = (it: Item) => {
    if (it.type === "milestone") return -1; // no progress at all (§7)
    const sub = subCounts?.get(it.id);
    if (sub && sub.total > 0) return Math.round((sub.done / sub.total) * 100);
    return it.status === "done" ? 100 : 0;
  };

  const rows = useMemo(() => {
    const visible = showCompleted ? items : items.filter((i) => i.status !== "done");
    const { key, dir } = sort;

    // Undated items stay at the bottom in both directions — flipping them to
    // the top on a reverse sort buries everything that actually has a date.
    if (key === "due" || key === "start") {
      const field = key === "due" ? "due" : "start";
      const dated = visible.filter((i) => i[field]);
      const undated = visible.filter((i) => !i[field]);
      dated.sort((a, b) => dir * (a[field]!.localeCompare(b[field]!)) || a.position - b.position);
      // Undated on this field, ordered by the other one, so a task with only a
      // deadline (common in Todoist) is not lost in list order.
      const other = field === "due" ? "start" : "due";
      undated.sort((a, b) =>
        (a[other] && b[other] ? a[other]!.localeCompare(b[other]!) : a[other] ? -1 : b[other] ? 1 : 0)
        || a.position - b.position);
      return [...dated, ...undated];
    }

    const rank = (i: Item): number | string => {
      switch (key) {
        case "title": return i.title.toLocaleLowerCase();
        case "type": return TYPE_ORDER.indexOf(i.type);
        case "priority": return PRIORITY_ORDER.indexOf(i.priority);
        case "phase": return phasePos.get(i.phase) ?? 0;
        case "status": return STATUS_ORDER.indexOf(i.status);
        case "progress": return progressOf(i);
      }
    };

    return [...visible].sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      const cmp = typeof ra === "string" ? ra.localeCompare(rb as string) : (ra as number) - (rb as number);
      return dir * cmp || a.position - b.position;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sort, showCompleted, phasePos, subCounts]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  if (items.length === 0) {
    return <div style={{ ...card, padding: "26px 20px", fontSize: 13.5, color: "var(--muted2)", textAlign: "center" }}>No items yet.</div>;
  }

  return (
    <div style={{ ...card, overflow: "hidden" }}>
      {/* §4.3 toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", borderBottom: "1px solid var(--line)" }}>
        <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>
          {rows.length} item{rows.length === 1 ? "" : "s"}
        </span>
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          title="Also set in Settings › Appearance"
          style={{
            marginLeft: "auto", padding: "4px 11px", borderRadius: 999, cursor: "pointer",
            border: `1px solid ${showCompleted ? "var(--accentLine)" : "var(--line2)"}`,
            background: showCompleted ? "var(--accentTint)" : "var(--surface)",
            color: showCompleted ? "var(--accent)" : "var(--muted)",
            fontSize: 11.5, fontWeight: 500,
          }}
        >
          Completed {showCompleted ? "shown" : "hidden"}
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: mobile ? 888 : "auto" }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: COLS, gap: 10, padding: "11px 18px", borderBottom: "1px solid var(--line)" }}>
            {HEADINGS.map((h, i) => {
              if (!h.key) return <div key={i} />;
              const on = sort.key === h.key;
              return (
                <button
                  key={i}
                  onClick={() => toggleSort(h.key!)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: 0, border: "none",
                    background: "none", cursor: "pointer", textAlign: "left",
                    fontSize: 10.5, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)",
                    textTransform: "uppercase", color: on ? "var(--accent)" : "var(--muted2)",
                  }}
                >
                  {h.label}
                  <span aria-hidden="true" style={{ fontSize: 9 }}>{on ? (sort.dir === 1 ? "↑" : "↓") : ""}</span>
                </button>
              );
            })}
          </div>

          {/* Rows */}
          {rows.map((it) => {
            const od = isOverdue(it);
            const dl = deadlineState(it);
            const done = it.status === "done";
            const sub = subCounts?.get(it.id);
            const isMilestone = it.type === "milestone";
            const prog = progressOf(it);
            const meta = STATUS_META[it.status];
            return (
              <div
                key={it.id}
                onClick={() => onOpen(it)}
                className="row-hover"
                style={{ display: "grid", gridTemplateColumns: COLS, gap: 10, padding: "9px 18px", alignItems: "center", cursor: "pointer", borderBottom: "1px solid var(--tint2)", background: od ? "var(--badTint)" : "transparent" }}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); onToggle?.(it); }}
                  aria-label={done ? "Mark incomplete" : "Mark complete"}
                  style={{ width: 20, height: 20, flex: "none", borderRadius: "50%", border: done ? "none" : "1.5px solid var(--line2)", background: done ? "var(--good)" : "transparent", color: "var(--onAccent)", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                >
                  {done ? "✓" : ""}
                </button>

                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: done ? "line-through" : "none", color: done ? "var(--muted)" : "var(--ink)" }}>{it.title}</span>
                  {it.clientVisible && <span title="On the client report" style={{ color: "var(--accent)", fontSize: 11, flex: "none" }}>◉</span>}
                  {it.recur !== "none" && <span className="mono" style={{ fontSize: 10, color: "var(--muted2)", flex: "none" }}>↻ {RECUR_BADGE[it.recur]}</span>}
                </span>

                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "var(--ink3)", minWidth: 0 }}>
                  <span style={{ color: TYPE_META[it.type].color, flex: "none" }}>{TYPE_META[it.type].glyph}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{TYPE_META[it.type].label}</span>
                </span>

                <span style={{ fontSize: 12.5, color: "var(--ink3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{phaseName.get(it.phase) || "—"}</span>

                {/* Inline status — a pill that happens to be a select. */}
                <span onClick={(e) => e.stopPropagation()}>
                  <select
                    value={it.status}
                    onChange={(e) => onStatusChange?.(it, e.target.value as ItemStatus)}
                    disabled={!onStatusChange}
                    aria-label="Status"
                    style={{
                      maxWidth: "100%", padding: "3px 8px", borderRadius: 999, border: "1px solid transparent",
                      background: meta.bg, color: meta.text, fontSize: 11, fontWeight: 500,
                      cursor: onStatusChange ? "pointer" : "default", appearance: "none",
                    }}
                  >
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                  </select>
                </span>

                {/* The scheduled day, and ONLY when Todoist has one. Blank
                    means unscheduled — it does not mean "look at the deadline
                    instead". The red wash on a late row comes from this date. */}
                <span className="mono" style={{ fontSize: 12, color: od ? "var(--bad)" : "var(--ink3)" }}>
                  {it.start ? shortDate(it.start) : "—"}
                </span>

                {/* The deadline — Todoist's `deadline`, never its date. */}
                <span onClick={(e) => e.stopPropagation()}>
                  <DeadlineCell item={it} state={dl} onChange={onDueChange} />
                </span>

                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  {isMilestone ? (
                    <span className="mono" style={{ fontSize: 12, color: "var(--muted2)" }}>—</span>
                  ) : (
                    <>
                      <span style={{ flex: 1 }}><ProgressBar value={prog} color={od ? "var(--bad)" : done ? "var(--good)" : "var(--accent)"} /></span>
                      <span className="mono" style={{ fontSize: 10.5, color: "var(--muted)", flex: "none" }}>{sub && sub.total > 0 ? `${sub.done}/${sub.total}` : `${prog}%`}</span>
                    </>
                  )}
                </span>

                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink3)" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: PRIORITY_META[it.priority].color, flex: "none" }} />
                  {PRIORITY_META[it.priority].label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * One deadline cell.
 *
 * An empty `<input type="date">` is not empty on screen: every browser paints
 * its own mask into it, so a column of tasks that have no deadline reads as a
 * column that has one — a date-shaped thing on every row, in a column headed
 * "Deadline". Trying to dim the mask only works on WebKit and still leaves the
 * cell looking like a value.
 *
 * So an absent deadline renders as a plain "—", and the picker is summoned by
 * clicking it. Nothing date-shaped appears until there is an actual date.
 */
function DeadlineCell({
  item, state, onChange,
}: {
  item: Item;
  state: DeadlineState | null;
  onChange?: (i: Item, due: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const color =
    state === "overdue" ? "var(--bad)"
    : state === "today" || state === "soon" ? "var(--warn)"
    : "var(--ink3)";

  if (!onChange) {
    return <span className="mono" style={{ fontSize: 12, color }}>{item.due ? shortDate(item.due) : "—"}</span>;
  }

  if (!item.due && !editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Set a deadline"
        aria-label="Set a deadline"
        className="mono"
        style={{
          width: "100%", textAlign: "left", padding: "2px 4px", borderRadius: 6,
          border: "1px solid transparent", background: "transparent",
          color: "var(--muted3)", fontSize: 12, cursor: "pointer",
        }}
      >—</button>
    );
  }

  return (
    <input
      type="date"
      autoFocus={editing && !item.due}
      value={item.due ? item.due.slice(0, 10) : ""}
      onChange={(e) => onChange(item, e.target.value)}
      onBlur={() => setEditing(false)}
      aria-label="Deadline"
      className="mono"
      style={{
        width: "100%", padding: "2px 4px", borderRadius: 6, border: "1px solid transparent",
        background: "transparent", fontSize: 12, color, cursor: "pointer",
      }}
    />
  );
}
